import { FC, useCallback, useMemo, useState } from 'react'
import * as Icons from '@ant-design/icons'
import _ from 'lodash'
import { useFormContext } from 'react-hook-form'

import { Select, LabeledValue } from 'admin/lib/Forms'
import { Queries, APITypes } from 'admin/lib/Api'
import { Waste } from 'admin/lib/Records'

export interface CarrierPresetSelectProps {
  onChange: () => void
  disabled?: boolean
  hidden?: boolean
  enriched?: boolean
  instrumented?: boolean
}

type CarrierOption = LabeledValue & {
  carrier: APITypes.WasteCarrier
}

export const CarrierPresetSelect: FC<CarrierPresetSelectProps> = ({
  onChange,
  disabled = false,
  hidden = false
}) => {
  const form = useFormContext()

  const removalDate = form.getValues('data.removalDate')
  const [search, setSearch] = useState<string | null>(null)
  const query = Queries.useWasteCarriers(search, removalDate)

  const options = useMemo(() => {
    if (!query.data?.pages) {
      return []
    }

    return _.flatMap(
      query.data.pages,
      page => page?.items ?? []
    ).map<CarrierOption>(carrier => {
      return {
        value: carrier.carrierLicenceId,
        label: `${carrier.name} (${carrier.carrierLicenceId})`,
        carrier
      }
    })
  }, [query.data?.pages])

  const handleSelect = useCallback(
    (label, value) => {
      const option = value as CarrierOption

      form.setValue(
        Waste.enriched.carrierLicenceId.path,
        option.carrier.carrierLicenceId
      )

      form.setValue(Waste.enriched.carrierName.path, option.carrier.name)

      if (onChange) {
        onChange()
      }
    },
    [form, onChange]
  )

  type HandleFocusEvent = React.FocusEvent<HTMLInputElement>
  const handleFocus = (e: HandleFocusEvent) => {
    performance.mark('field-entered', {
      detail: { fieldName: 'wasteDataCarrierSelector' }
    })
  }

  type HandleBlurEvent = React.FocusEvent<HTMLInputElement>
  const handleBlur = (e: HandleBlurEvent) => {
    performance.mark('field-exited', {
      detail: { fieldName: 'wasteDataCarrierSelector' }
    })
    performance.measure('field-duration', {
      start: 'field-entered',
      end: 'field-exited',
      detail: { fieldName: 'wasteDataCarrierSelector' }
    })
  }

  if (hidden) {
    return null
  }

  return (
    <Select<CarrierOption>
      id="wasteDataCarrierSelector"
      showSearch
      loading={query.isFetching}
      placeholder={query.isFetching ? 'Fetching' : 'Select a Carrier to apply'}
      filter={false}
      options={options}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onSearch={setSearch}
      onBottomReached={query.hasNextPage ? query.fetchNextPage : undefined}
      labelInValue
      onSelect={handleSelect}
      suffixIcon={<Icons.SearchOutlined />}
      disabled={disabled}
    />
  )
}
